import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "fence-deck-web-design-castle-rock",
  metaTitle: 'Fence & Deck Builder Web Design in Castle Rock, CO | Websites That Win Bids',
  metaDescription: 'Websites for Castle Rock fence and deck builders — material pages, HOA guidance, and project galleries built to win Douglas County quote requests. Call (719) 419-3935.',
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Castle Rock web design company", to: "/web-design-castle-rock-colorado" },
  ],
  currentLabel: "Fence & Deck Web Design Castle Rock",
  eyebrow: "Fence & Deck Web Design — Castle Rock, CO",
  h1: (
    <>
      Fence & Deck <span className="text-brand-gold">Web Design</span> in Castle Rock
    </>
  ),
  heroDescription: "Fast, mobile-friendly websites for Castle Rock fence and deck contractors, built to turn local searches into quote requests.",
  intro: 'Castle Rock neighborhoods have HOA rules, big backyards, and buyers who research before they call. We build fence and deck websites with a page for every material, HOA-friendly guidance, and galleries of your real work, so homeowners trust you before the first visit.',
  definition: {
    term: "Fence & Deck web design in Castle Rock",
    body: "A fence and deck website built for Castle Rock buyers: one page per service, real project photos, clear calls to action, and local town pages so Douglas County homeowners can find and trust you.",
  },
  whatWeDo: [
    { title: "A page for every service", body: 'Wood, vinyl, composite, privacy, ranch fencing, and custom decks — each gets its own page so it can show up for its own search.' },
    { title: "Real project galleries", body: "Your own finished work, shown clearly on phones and desktops." },
    { title: "Simple quote forms", body: 'Quote forms that ask for linear feet or deck size, material, and timing so you only chase real buyers.' },
    { title: "Douglas County town pages", body: "Castle Rock, Castle Pines, Parker, Larkspur, and Franktown pages for the areas you serve." },
    { title: "Google Maps ready", body: "Your site and Google Business Profile tell the same story, so you show up in local results." },
    { title: "SEO-ready from launch", body: "Clean structure, local business details, and fast pages built in from day one." },
  ],
  whyThisMarket: [
    { heading: 'HOA questions bring early traffic', body: 'Homeowners in The Meadows and Crystal Valley look up HOA fence and deck rules before hiring. A clear guide puts you in front of them first.' },
    { heading: 'Material pages beat one big list', body: 'Composite decks and cedar fences have different buyers. Separate pages help each one show up.' },
    { heading: 'Galleries close the bid', body: 'Fence and deck work sells on finished photos. Project galleries near the top of the page help win the estimate.' },
  ],
  relatedLinks: [
    { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
    { to: "/castle-rock", label: "Castle Rock digital marketing" },
    { to: "/south-metro-contractor-marketing", label: "South Metro contractor marketing hub" },
    { to: "/learn/fence-deck-leads-colorado", label: "Fence & Deck lead guide for Colorado" },
    { to: "/fencing-web-design-colorado", label: "Fence & Deck web design across Colorado" },
    { to: "/learn/organic-leads-colorado-contractors", label: "Free leads first: a guide for Colorado contractors" },
    { to: "/pricing", label: "Website pricing" },
  ],
  faqs: [
    { q: "How much does a fence and deck website cost in Castle Rock?", a: "Website packages start at $1,850 one-time and include one year of hosting. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take?", a: "Typical sites are ready in under seven business days once we have your photos and service details." },
    { q: "Do you serve areas outside Castle Rock?", a: "Yes — Castle Pines, Parker, Larkspur, Franktown, Highlands Ranch, and the rest of the south metro and Front Range." },
  ],
  serviceName: "Fence & Deck Web Design in Castle Rock, CO",
  serviceDescription: "Mobile-friendly websites for fence and deck contractors in Castle Rock and Douglas County.",
  areaServed: "Castle Rock, CO",
};

export const Route = createFileRoute("/fence-deck-web-design-castle-rock")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
